import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useRealtimeRefresh, { eventPrefix } from '../../realtime/useRealtimeRefresh';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Link as MuiLink, Stack, TextField, Tooltip, Typography,
  alpha, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { PipelinesIcon, ModelsIcon, SegmentsIcon } from '../global/CustomIcons';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import ComputerIcon from '@mui/icons-material/Computer';
import { ResponsiveLine } from '@nivo/line';
import { format as fnsFormat, formatDistanceToNow } from '../../utils/datetime';
import { Chip } from '@mui/material';

import { fetchRequest as fetchModels } from '../../redux/actions/models/list';
import { useTheme as useCustomTheme } from '../../theme/ThemeContext';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import DataTable from '../common/DataTable';
import ListingToolbar from '../common/ListingToolbar';
import EntityCell from '../common/EntityCell';
import RowActions from '../common/RowActions';
import { CMButton } from '../../containers/styledComponents';

const safeFormatDate = (value, fmt = 'MMM d, h:mm a', fallback = 'Never') => {
  if (!value) return fallback;
  try { return fnsFormat(new Date(value), fmt); } catch (_e) { return fallback; }
};

const safeRelative = (value) => {
  try { if (!value) return null; return `${formatDistanceToNow(new Date(value))} ago`; }
  catch (_e) { return null; }
};

// Compact sparkline of training-score history. Same chart contract
// as the original ModelsPage card, shrunk to fit a table cell.
const PerformanceTrendSparkline = ({ data }) => {
  const theme = useTheme();
  let trend;
  try {
    trend = Array.isArray(data) ? data : (data ? JSON.parse(data) : []);
    if (!Array.isArray(trend)) trend = [];
  } catch (_e) { trend = []; }

  if (trend.length === 0) {
    return (
      <Box sx={{ height: 40, display: 'flex', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
          No history yet
        </Typography>
      </Box>
    );
  }

  const sorted = [...trend].sort(
    (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)
  );

  const chartData = [{
    id: 'performance',
    data: sorted.map((item, idx) => ({
      x: idx + 1,
      y: typeof item === 'object' && item.score !== undefined ? item.score
        : typeof item === 'number' ? item : 0,
      created: typeof item === 'object' && item.created_at
        ? new Date(item.created_at).toLocaleDateString() : '',
    })),
  }];

  return (
    <Box sx={{ height: 40, minWidth: 140 }}>
      <ResponsiveLine
        data={chartData}
        margin={{ top: 4, right: 4, bottom: 2, left: 4 }}
        xScale={{ type: 'point' }}
        yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
        axisTop={null} axisRight={null} axisBottom={null} axisLeft={null}
        enablePoints={false}
        enableGridX={false}
        enableGridY={false}
        colors={[theme.palette.primary.main]}
        lineWidth={1.5}
        curve="monotoneX"
        useMesh
        animate={false}
        theme={{
          tooltip: {
            container: {
              background: theme.palette.background.paper,
              color: theme.palette.text.primary,
              fontSize: 12, borderRadius: 4,
              boxShadow: theme.palette.boxShadow,
              padding: '6px 10px',
              border: `1px solid ${theme.palette.divider}`,
            },
          },
        }}
        tooltip={({ point }) => (
          <div style={{
            background: theme.palette.background.paper,
            padding: '6px 10px',
            border: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
            boxShadow: theme.palette.boxShadow,
          }}>
            <div style={{ fontSize: 11 }}>
              <strong>{point.data.y.toFixed(2)}%</strong>{point.data.created ? ` — ${point.data.created}` : ''}
            </div>
          </div>
        )}
      />
    </Box>
  );
};

// In-row badge indicating training environment (cloud / local).
const PlatformBadge = ({ platform }) => {
  const theme = useTheme();
  if (!platform) return null;
  const lower = String(platform).toLowerCase();
  const isCloud = lower === 'cloud';
  const isLocal = lower === 'local';
  if (!isCloud && !isLocal) return null;
  const Icon = isCloud ? CloudOutlinedIcon : ComputerIcon;
  const color = isCloud ? theme.palette.info.main : theme.palette.warning.main;
  return (
    <Tooltip title={isCloud ? 'Cloud' : 'Local'} arrow placement="top">
      <Icon sx={{ fontSize: 12, color, opacity: 0.85 }} />
    </Tooltip>
  );
};

const ModelsPage = ({ fetchModels, models, isLoading, error, pagination }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { isDarkMode } = useCustomTheme();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState([]);
  const [, forceUpdate] = useState({});

  // Local mirror of the redux ``models`` slice. Reduces table flicker
  // when the 30s silent refresh repeats and the result is identical.
  const [localModels, setLocalModels] = useState([]);
  const isFirstRenderRef = useRef(true);
  const isRefreshingRef = useRef(false);
  const prevModelsRef = useRef(null);

  useEffect(() => {
    if (models && (!prevModelsRef.current
        || JSON.stringify(prevModelsRef.current) !== JSON.stringify(models))) {
      setLocalModels(models);
      prevModelsRef.current = models;
    }
  }, [models]);

  useEffect(() => { forceUpdate({}); }, [isDarkMode]);

  const silentRefresh = useCallback(() => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;
    try {
      dispatch({
        type: 'SILENT_MODELS_LIST_REQUEST',
        payload: { page, rowsPerPage, sortColumn, sortDirection },
      });
    } catch (e) {
      console.error('Silent refresh error:', e);
    } finally {
      setTimeout(() => { isRefreshingRef.current = false; }, 1000);
    }
  }, [dispatch, page, rowsPerPage, sortColumn, sortDirection]);

  useEffect(() => {
    fetchModels({ page, rowsPerPage, sortColumn, sortDirection });
    isFirstRenderRef.current = false;
  }, [fetchModels, page, rowsPerPage, sortColumn, sortDirection]);

  // Live (no polling): a model's row changes when a training job transitions
  // (queued/started/completed/failed) or a deployment updates. Refresh on
  // those workspace-channel events; reconnect (msg=null) reconciles too.
  useRealtimeRefresh(
    eventPrefix('training.', 'deployment.'),
    silentRefresh,
  );

  useEffect(() => { setPage(0); }, [search, stateFilter]);

  const handleCreatePipeline = useCallback(() => navigate('/pipelines/create'), [navigate]);
  const handleExploreModel = useCallback((m) => navigate(`/pipeline/${m.uid}`), [navigate]);
  const handleNavigateToSegment = useCallback((segmentId, e) => {
    e?.stopPropagation();
    navigate(`/segments/${segmentId}`);
  }, [navigate]);
  const handleNavigateToBlueprint = useCallback((blueprintId, e) => {
    e?.stopPropagation();
    navigate(`/model/${blueprintId}`);
  }, [navigate]);

  // Resolve the pipeline state ('training' / 'active' / 'idle') used
  // both for the EntityCell stripe and the state filter.
  const resolveState = (m) => {
    if (m.in_training) return 'training';
    if (m.active) return 'active';
    return 'idle';
  };

  const STATE_TONE = { training: 'info', active: 'success', idle: 'neutral' };
  const STATE_LABEL = { training: 'Training', active: 'Active', idle: 'Idle' };

  // Client-side filter on top of the server-paginated current page.
  // Cross-page search would need a backend ``q`` param.
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return localModels.filter((m) => {
      const state = resolveState(m);
      if (stateFilter.length > 0 && !stateFilter.includes(state)) return false;
      if (!q) return true;
      return (m.name || '').toLowerCase().includes(q)
        || (m.uid || '').toLowerCase().includes(q)
        || (m.segment_info?.name || m.segment || '').toLowerCase().includes(q)
        || (m.blueprint_info?.name || m.blueprint || '').toLowerCase().includes(q);
    });
  }, [localModels, search, stateFilter]);

  const scoreColor = (s) => {
    if (typeof s !== 'number') return theme.palette.text.secondary;
    if (s >= 80) return theme.palette.success.main;
    if (s >= 60) return theme.palette.primary.main;
    if (s >= 40) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const toggleState = (s) =>
    setStateFilter((prev) => prev.includes(s) ? prev.filter((v) => v !== s) : [...prev, s]);

  const activeFilterCount = (search ? 1 : 0) + (stateFilter.length > 0 ? 1 : 0);
  const clearAll = () => { setSearch(''); setStateFilter([]); };

  const columns = [
    {
      key: 'identity',
      label: 'ID',
      width: 130,
      render: (m) => {
        const s = resolveState(m);
        return <EntityCell id={m.uid} tone={STATE_TONE[s]} label={STATE_LABEL[s]} />;
      },
    },
    {
      key: 'name',
      label: 'Pipeline',
      sortable: true,
      width: '28%',
      render: (m) => (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography variant="body2" sx={{
              fontWeight: 600, fontSize: 13, lineHeight: 1.3,
              maxWidth: 280,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {m.name || m.uid}
            </Typography>
            {m.blueprint_info?.metadata?.training_environment && (
              <PlatformBadge platform={m.blueprint_info.metadata.training_environment} />
            )}
            {m.has_pending_promotion && (
              <Tooltip title="A newer training beat the serving version — open the pipeline to promote or keep current" arrow placement="top">
                <Box component="span" sx={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  px: 0.5,
                  py: 0.05,
                  borderRadius: 0.5,
                  bgcolor: alpha(theme.palette.warning.main, 0.12),
                  color: theme.palette.warning.main,
                }}>
                  REVIEW
                </Box>
              </Tooltip>
            )}
          </Box>
          <Stack direction="row" spacing={1.5} sx={{ mt: 0.4, alignItems: 'center' }}>
            {m.segment && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, minWidth: 0 }}>
                <SegmentsIcon sx={{ fontSize: 11, color: theme.palette.text.disabled }} />
                <MuiLink
                  component="button" type="button" underline="hover"
                  onClick={(e) => handleNavigateToSegment(m.segment, e)}
                  sx={{
                    fontSize: 11,
                    color: theme.palette.text.secondary,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    maxWidth: 140,
                    textAlign: 'left',
                  }}
                >
                  {m.segment_info?.name || m.segment}
                </MuiLink>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, minWidth: 0 }}>
              <ModelsIcon sx={{ fontSize: 11, color: theme.palette.text.disabled }} />
              {(() => {
                const isFoundation = !!m.foundation;
                const label = isFoundation
                  ? (() => {
                      const f = m.foundation;
                      const slug = f.slug || f.version;
                      if (slug) return `${f.family || 'foundation'} · ${slug}`;
                      return f.uid || 'Foundation';
                    })()
                  : (m.blueprint_info?.name || m.blueprint || '—');
                const targetId = isFoundation ? m.foundation?.uid : m.blueprint;
                if (!targetId) {
                  return (
                    <Typography variant="caption" sx={{
                      fontSize: 11, color: theme.palette.text.secondary,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      maxWidth: 160,
                    }}>
                      {label}
                    </Typography>
                  );
                }
                return (
                  <MuiLink
                    component="button" type="button" underline="hover"
                    onClick={(e) => handleNavigateToBlueprint(targetId, e)}
                    sx={{
                      fontSize: 11,
                      color: theme.palette.text.secondary,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      maxWidth: 160,
                      textAlign: 'left',
                    }}
                  >
                    {label}
                  </MuiLink>
                );
              })()}
            </Box>
          </Stack>
        </Box>
      ),
    },
    {
      key: 'last_training_time',
      label: 'Last Training',
      sortable: true,
      width: 150,
      render: (m) => (
        <Tooltip title={safeFormatDate(m.last_training_time, 'PPpp', 'Never')} arrow placement="top">
          <Box>
            <Typography variant="body2" sx={{ fontSize: 13 }}>
              {safeFormatDate(m.last_training_time, 'MMM d, h:mm a', 'Never')}
            </Typography>
            {m.last_training_time && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                {safeRelative(m.last_training_time)}
              </Typography>
            )}
          </Box>
        </Tooltip>
      ),
    },
    {
      key: 'best_score',
      label: 'Best Score',
      width: 90,
      align: 'right',
      render: (m) => {
        const v = m.top_model?.score;
        return (
          <Typography variant="body2" sx={{
            color: scoreColor(v),
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            fontSize: 13,
          }}>
            {typeof v === 'number' ? `${v.toFixed(1)}%` : '—'}
          </Typography>
        );
      },
    },
    {
      key: 'performance',
      label: 'Trend',
      width: 160,
      render: (m) => <PerformanceTrendSparkline data={m.performance_trend} />,
    },
    {
      key: 'actions',
      label: '',
      width: 60,
      align: 'right',
      stopRowClick: true,
      render: (m) => (
        <RowActions onOpen={() => handleExploreModel(m)} openLabel="Open pipeline" />
      ),
    },
  ];

  if (error) {
    return (
      <Box sx={{ width: '100%' }}>
        <PageBreadcrumbs items={[{ text: 'Pipelines', icon: <PipelinesIcon /> }]} sx={{ mb: 2 }} />
        <Box sx={{ p: 3, bgcolor: alpha(theme.palette.error.main, 0.08), color: theme.palette.error.main, borderRadius: 1 }}>
          <Typography variant="h6">Error Loading Pipelines</Typography>
          <Typography variant="body2">{String(error)}</Typography>
        </Box>
      </Box>
    );
  }

  const filtersNode = (
    <>
      <TextField
        size="small"
        placeholder="Search pipelines on this page…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />,
        }}
        sx={{ minWidth: 280, flex: 1 }}
      />
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="caption" color="text.secondary" sx={{
          fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', mr: 0.5,
        }}>
          State
        </Typography>
        {[
          { value: 'training', label: 'Training', color: 'info'    },
          { value: 'active',   label: 'Active',   color: 'success' },
          { value: 'idle',     label: 'Idle',     color: 'default' },
        ].map((opt) => {
          const selected = stateFilter.includes(opt.value);
          return (
            <Chip
              key={opt.value}
              label={opt.label}
              size="small"
              onClick={() => toggleState(opt.value)}
              color={selected ? opt.color : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
          );
        })}
      </Stack>
    </>
  );

  const actionsNode = (
    <CMButton startIcon={<AddIcon />} onClick={handleCreatePipeline} size="small">
      New Pipeline
    </CMButton>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <PageBreadcrumbs items={[{ text: 'Pipelines', icon: <PipelinesIcon /> }]} mb={0.5} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, ml: 1 }}>
        A Pipeline pairs a Model with a Segment and a schedule, then trains automatically. Refreshes every 30 seconds.
      </Typography>

      <ListingToolbar
        filters={filtersNode}
        actions={actionsNode}
        activeCount={activeFilterCount}
        onClearFilters={clearAll}
      />

      <DataTable
        columns={columns}
        rows={filteredRows}
        getRowKey={(row) => row.uid}
        onRowClick={(row) => handleExploreModel(row)}
        loading={isLoading && isFirstRenderRef.current}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSortChange={(key, dir) => { setSortColumn(key); setSortDirection(dir); }}
        page={page}
        rowsPerPage={rowsPerPage}
        totalItems={pagination?.total_items || 0}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(0); }}
        rowsPerPageOptions={[25, 50, 100]}
        emptyState={{
          icon: <PipelinesIcon sx={{ fontSize: 32 }} />,
          title: activeFilterCount > 0 ? 'No pipelines match your filters' : 'No Pipelines Yet',
          message: activeFilterCount > 0
            ? 'Try a different search or clear the state filter.'
            : 'A Pipeline pairs a Model (template or pretrained foundation model) with a segment and a schedule, then trains automatically.',
          action: activeFilterCount > 0 ? null : (
            <CMButton startIcon={<AddIcon />} onClick={handleCreatePipeline}>
              Create Your First Pipeline
            </CMButton>
          ),
        }}
      />
    </Box>
  );
};

const mapStateToProps = (state) => ({
  models: state.models_list.response.pipelines,
  isLoading: state.models_list.isLoading,
  error: state.models_list.error,
  pagination: state.models_list.response.pagination,
});

const mapDispatchToProps = (dispatch) => ({
  fetchModels: (params) => dispatch(fetchModels(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ModelsPage);
